import React, { useState, useRef, useMemo } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Download,
  Copy,
  Check,
  Search,
  Printer,
  FileText,
  Edit3,
  Eye,
  AlertTriangle,
  CheckCircle2,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  FileType2,
} from 'lucide-react';
import { theme } from '../styles/theme';
import { FinalReport } from '../types';
import {
  buildReadinessChecks,
  downloadDocx,
  hasBlockingFailures,
  ReadinessCheck,
} from '../utils/finalReportExport';

/**
 * Phase C — print stylesheet.
 *
 * Scopes the print output to the report content only — hides the app sidebar,
 * the tabs, the toolbar, and any element tagged `data-print="hide"`. Renders
 * the markdown content full-width with serif type for readability when
 * printed or saved as PDF. The print button now produces a usable artefact.
 */
const PrintStyles = createGlobalStyle`
  /* Phase D — search highlight: applied via attribute toggle, not inline style.
     Survives re-renders cleanly; see scrollToSection. */
  [data-final-report-highlight] {
    background-color: #fef3c7 !important;
    transition: background-color 0.3s ease;
    border-radius: 3px;
    padding: 1px 2px;
  }

  @media print {
    @page { margin: 24mm 18mm; }
    html, body { background: white !important; }
    body * { visibility: hidden; }
    [data-print-root="final-report"], [data-print-root="final-report"] * {
      visibility: visible;
    }
    [data-print-root="final-report"] {
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
      box-shadow: none !important;
      border: none !important;
    }
    [data-print="hide"] { display: none !important; }
    /* Promote content for clean line spacing on the page. */
    [data-print-root="final-report"] h1,
    [data-print-root="final-report"] h2,
    [data-print-root="final-report"] h3 { page-break-after: avoid; }
    [data-print-root="final-report"] p,
    [data-print-root="final-report"] li { font-family: Georgia, 'Times New Roman', serif; font-size: 11pt; line-height: 1.55; }
  }
`;

const ViewerContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 80vh;
  background: ${theme.colors.surface};
  overflow: auto;
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${theme.spacing[4]} ${theme.spacing[6]};
  background: linear-gradient(135deg, 
    ${theme.colors.backgroundSecondary}, 
    ${theme.colors.surface}
  );
  border-bottom: 1px solid ${theme.colors.border};
  backdrop-filter: blur(10px);
  flex-shrink: 0;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, 
      transparent, 
      ${theme.colors.primary[500]}40, 
      transparent
    );
  }
`;

const ToolbarLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
`;

const ToolbarTitle = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  color: ${theme.colors.textPrimary};
  font-weight: ${theme.typography.fontWeight.bold};
  font-size: ${theme.typography.fontSize.base};
  letter-spacing: 0.3px;
  
  svg {
    color: ${theme.colors.primary[500]};
  }
`;

const ToolbarRight = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
`;

const ToolbarButton = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[1]};
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  background: ${({ $variant }) => 
    $variant === 'primary' 
      ? theme.colors.primary[500] 
      : 'transparent'
  };
  color: ${({ $variant }) => 
    $variant === 'primary' 
      ? 'white' 
      : theme.colors.textSecondary
  };
  border: 1px solid ${({ $variant }) => 
    $variant === 'primary' 
      ? theme.colors.primary[500] 
      : theme.colors.border
  };
  border-radius: ${theme.borderRadius.sm};
  font-size: ${theme.typography.fontSize.xs};
  cursor: pointer;
  transition: all ${theme.transitions.base};

  &:hover {
    background: ${({ $variant }) =>
      $variant === 'primary'
        ? theme.colors.primary[600]
        : theme.colors.backgroundSecondary
    };
    border-color: ${({ $variant }) =>
      $variant === 'primary'
        ? theme.colors.primary[600]
        : theme.colors.borderHover
    };
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const ReadinessPanel = styled.div`
  padding: ${theme.spacing[3]} ${theme.spacing[6]};
  background: ${theme.colors.backgroundSecondary};
  border-bottom: 1px solid ${theme.colors.border};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

const ReadinessHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.textPrimary};
`;

const ReadinessList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[1]};
`;

const ReadinessRow = styled.div<{ $severity: 'pass' | 'warn' | 'fail' }>`
  display: flex;
  gap: ${theme.spacing[2]};
  align-items: flex-start;
  padding: ${theme.spacing[2]};
  border-radius: ${theme.borderRadius.sm};
  background: ${({ $severity }) => {
    switch ($severity) {
      case 'pass':
        return theme.colors.success[100] + '50';
      case 'warn':
        return theme.colors.warning[100] + '60';
      case 'fail':
        return theme.colors.error[100] + '50';
    }
  }};
  color: ${({ $severity }) => {
    switch ($severity) {
      case 'pass':
        return theme.colors.success[700];
      case 'warn':
        return theme.colors.warning[700];
      case 'fail':
        return theme.colors.error[700];
    }
  }};
  font-size: ${theme.typography.fontSize.sm};
  line-height: 1.4;
  > svg {
    flex-shrink: 0;
    margin-top: 2px;
  }
`;

const SearchContainer = styled.div`
  position: relative;
  width: 200px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: ${theme.spacing[2]} ${theme.spacing[2]} ${theme.spacing[2]} ${theme.spacing[8]};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.sm};
  font-size: ${theme.typography.fontSize.xs};
  background: ${theme.colors.surface};
  color: ${theme.colors.textPrimary};
  transition: all ${theme.transitions.base};

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 2px ${theme.colors.primary[500]}20;
  }

  &::placeholder {
    color: ${theme.colors.textMuted};
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: ${theme.spacing[2]};
  top: 50%;
  transform: translateY(-50%);
  color: ${theme.colors.textMuted};
  pointer-events: none;
`;

const ContentArea = styled.div`
  flex: 1;
  position: relative;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

const MarkdownContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  background: linear-gradient(135deg, #fafafa, #ffffff);
  font-family: 'Inter', 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  position: relative;
  min-height: 0;
  max-height: 100%;
  
  /* Ensure consistent scrolling */
  
  /* Enhanced custom scrollbar for better visibility */
  &::-webkit-scrollbar {
    width: 14px;
    background: transparent;
  }
  
  &::-webkit-scrollbar-track {
    background: linear-gradient(180deg, #f1f3f4, #e8eaed);
    border-radius: ${theme.borderRadius.lg};
    margin: ${theme.spacing[2]} 0;
    border: 1px solid #dadce0;
    box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.1);
  }
  
  &::-webkit-scrollbar-thumb {
    background: linear-gradient(180deg, 
      ${theme.colors.primary[500]}, 
      ${theme.colors.primary[700]}
    );
    border-radius: ${theme.borderRadius.lg};
    border: 2px solid #f1f3f4;
    transition: all ${theme.transitions.base};
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
    min-height: 30px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(180deg, 
      ${theme.colors.primary[600]}, 
      ${theme.colors.primary[800]}
    );
    border-color: #e8eaed;
    box-shadow: 0 3px 8px rgba(0, 0, 0, 0.2);
    transform: scale(1.1);
  }
  
  &::-webkit-scrollbar-thumb:active {
    background: linear-gradient(180deg, 
      ${theme.colors.primary[700]}, 
      ${theme.colors.primary[900]}
    );
    transform: scale(0.95);
  }
  
  /* Scrolling is now properly enabled */
  
  /* Add subtle pattern overlay */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: radial-gradient(circle at 1px 1px, rgba(0,0,0,0.015) 1px, transparent 0);
    background-size: 24px 24px;
    pointer-events: none;
    z-index: 0;
  }
`;

const MarkdownContent = styled.div`
  padding: ${theme.spacing[6]};
  line-height: 1.6;
  max-width: none;
  color: #333;
  position: relative;
  z-index: 1;
  font-family: 'Inter', 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  min-height: 100%;
  flex: 1;

  h1 {
    color: #1a1a1a;
    font-size: 1.8rem;
    font-weight: 700;
    margin: 0 0 ${theme.spacing[3]} 0;
    padding-bottom: ${theme.spacing[2]};
    border-bottom: 2px solid ${theme.colors.primary[500]};
    background: linear-gradient(135deg, #1a1a1a, ${theme.colors.primary[600]});
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  h2 {
    color: ${theme.colors.primary[700]};
    font-size: 1.4rem;
    font-weight: 600;
    margin: ${theme.spacing[4]} 0 ${theme.spacing[2]} 0;
    display: flex;
    align-items: center;
    gap: ${theme.spacing[2]};
    position: relative;
    padding-left: ${theme.spacing[3]};
    
    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 50%;
      transform: translateY(-50%);
      width: 3px;
      height: 80%;
      background: linear-gradient(180deg, ${theme.colors.primary[500]}, ${theme.colors.accent[500]});
      border-radius: ${theme.borderRadius.full};
    }
  }

  h3 {
    color: ${theme.colors.primary[600]};
    font-size: 1.1rem;
    font-weight: 600;
    margin: ${theme.spacing[3]} 0 ${theme.spacing[2]} 0;
  }

  p {
    margin: 0 0 ${theme.spacing[2]} 0;
    color: #4a5568;
    line-height: 1.6;
    font-size: ${theme.typography.fontSize.sm};
  }

  ul, ol {
    margin: 0 0 ${theme.spacing[2]} 0;
    padding-left: ${theme.spacing[5]};
  }

  li {
    margin-bottom: ${theme.spacing[1]};
    color: #4a5568;
    font-size: ${theme.typography.fontSize.sm};
    line-height: 1.5;
    
    &::marker {
      color: ${theme.colors.primary[500]};
    }
  }

  strong {
    color: #2c3e50;
    font-weight: 600;
  }

  em {
    color: #7f8c8d;
    font-style: italic;
  }

  code {
    background: #f8f9fa;
    border: 1px solid #e9ecef;
    border-radius: 3px;
    padding: 2px 6px;
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
    font-size: 0.9em;
    color: #e83e8c;
  }

  pre {
    background: #f8f9fa;
    border: 1px solid #e9ecef;
    border-radius: ${theme.borderRadius.md};
    padding: ${theme.spacing[4]};
    overflow-x: auto;
    margin: ${theme.spacing[3]} 0;
    
    code {
      background: none;
      border: none;
      padding: 0;
      color: #333;
    }
  }

  blockquote {
    margin: ${theme.spacing[3]} 0;
    padding: ${theme.spacing[3]} ${theme.spacing[4]};
    background: #f8f9fa;
    border-left: 4px solid #007bff;
    color: #495057;
    font-style: italic;
  }

  hr {
    margin: ${theme.spacing[6]} 0;
    border: none;
    border-top: 1px solid #e1e1e1;
  }

  table {
    width: 100%;
    border-collapse: collapse;
    margin: ${theme.spacing[3]} 0;
    
    th, td {
      padding: ${theme.spacing[2]} ${theme.spacing[3]};
      text-align: left;
      border-bottom: 1px solid #e1e1e1;
    }
    
    th {
      background: #f8f9fa;
      font-weight: 600;
      color: #2c3e50;
    }
  }

  .line-numbers {
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 40px;
    background: #f1f1f1;
    border-right: 1px solid #e1e1e1;
    padding: ${theme.spacing[6]} ${theme.spacing[2]};
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
    font-size: 0.8rem;
    color: #999;
    line-height: 1.6;
    user-select: none;
    
    div {
      height: 1.6em;
    }
  }
`;

const StatusMessage = styled.div<{ $type: 'success' | 'info' }>`
  position: absolute;
  top: ${theme.spacing[4]};
  right: ${theme.spacing[4]};
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  background: ${({ $type }) => 
    $type === 'success' 
      ? theme.colors.success[500] 
      : theme.colors.primary[500]
  };
  color: white;
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.sm};
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  z-index: 10;
  animation: slideIn 0.3s ease-out;

  @keyframes slideIn {
    from {
      transform: translateY(-100%);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
`;

const EditableTextarea = styled.textarea`
  width: 100%;
  height: 100%;
  padding: ${theme.spacing[6]};
  border: none;
  outline: none;
  resize: none;
  font-family: 'JetBrains Mono', 'Fira Code', 'Monaco', 'Menlo', monospace;
  font-size: ${theme.typography.fontSize.sm};
  line-height: 1.6;
  color: #333;
  background: #f8f9fa;
  border-radius: 0;
  
  &:focus {
    background: #fff;
    box-shadow: inset 0 0 0 2px ${theme.colors.primary[500]}20;
  }
  
  &::placeholder {
    color: ${theme.colors.textMuted};
    font-style: italic;
  }
`;

interface FinalReportViewerProps {
  finalReport?: FinalReport;
  isLoading?: boolean;
  activeSection?: string;
  /** When true, the run was started in GENERATE mode — drafts were authored
   *  from blank responses. Swaps "Refined" → "Generated" in the rendered
   *  markdown headings and per-item subtitles. */
  isGenerateMode?: boolean;
}

const FinalReportViewer: React.FC<FinalReportViewerProps> = ({ finalReport, isLoading = false, activeSection, isGenerateMode = false }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [copied, setCopied] = useState(false);
  const [downloadStatus, setDownloadStatus] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editableContent, setEditableContent] = useState(finalReport?.full_report_markdown || '');
  const contentRef = useRef<HTMLDivElement>(null);
  // Phase C — readiness checklist visibility + waiver state. The waiver
  // unblocks Download when there are hard failures, but only after the
  // user has explicitly clicked through.
  const [checklistOpen, setChecklistOpen] = useState(false);
  const [downloadingDocx, setDownloadingDocx] = useState(false);
  const [docxError, setDocxError] = useState<string | null>(null);
  const [waivedFailures, setWaivedFailures] = useState(false);

  const readinessChecks: ReadinessCheck[] = useMemo(
    () => (finalReport ? buildReadinessChecks(finalReport) : []),
    [finalReport]
  );
  const hasFailures = hasBlockingFailures(readinessChecks);
  const downloadBlocked = hasFailures && !waivedFailures;

  const handleCopy = async () => {
    if (finalReport?.full_report_markdown) {
      await navigator.clipboard.writeText(finalReport.full_report_markdown);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleDownload = () => {
    if (downloadBlocked) {
      setChecklistOpen(true);
      return;
    }
    if (finalReport?.full_report_markdown) {
      const blob = new Blob([finalReport.full_report_markdown], { type: 'text/markdown' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `final-report-${finalReport.date}.md`;
      link.click();
      URL.revokeObjectURL(url);

      setDownloadStatus('Markdown downloaded.');
      setTimeout(() => setDownloadStatus(null), 3000);
    }
  };

  // Phase C — Word (.docx) export. Uses the structured FinalReport, not the
  // markdown projection, so drafted/excluded/compliance sections come through
  // even if the legacy markdown was truncated. Same readiness gate as .md.
  const handleDownloadDocx = async () => {
    if (downloadBlocked) {
      setChecklistOpen(true);
      return;
    }
    if (!finalReport) return;
    setDocxError(null);
    setDownloadingDocx(true);
    try {
      await downloadDocx(finalReport);
      setDownloadStatus('Word document downloaded.');
      setTimeout(() => setDownloadStatus(null), 3000);
    } catch (err) {
      console.error('docx export failed', err);
      setDocxError(err instanceof Error ? err.message : 'Word export failed');
    } finally {
      setDownloadingDocx(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  // Function to clean up raw JSON in markdown content + apply mode-aware
  // rewrites so generate-mode runs read as "Generated" instead of "Refined",
  // and inject the question text into headings where the backend rendered
  // an empty heading (e.g. "### 1.") because its _q_text helper couldn't
  // find the question on the item.
  const cleanMarkdownContent = (content: string): string => {
    if (!content) return content;

    // Strip raw JSON criteria array (rendered in the summary panel above).
    const criteriaRegex = /(📋 Evaluation Criteria)\s*\[[\s\S]*?\](?=\s*[✅✍️🎯📊💼🏆]|$)/g;
    let out = content.replace(criteriaRegex, '$1\n\n*See detailed evaluation criteria in the summary panel above.*');

    // Generate-mode rewrites. In generate mode the "Refined Tender Responses"
    // section is populated by AUTO-mode side-effect with refined_questions
    // entries that have empty `question` fields — they render as "### 1."
    // headings with no question text. Strip that section entirely; the real
    // generated content lives in the Drafted Responses section below.
    // Then rename "Drafted Responses (AI-authored…)" → "Generated Tender
    // Responses" so the user finds it where they expect.
    if (isGenerateMode) {
      // Remove the empty-headed Refined section: from its ## heading up to
      // the next ## heading or end-of-string.
      out = out.replace(
        /##\s*✍️?\s*Refined Tender Responses[\s\S]*?(?=\n##\s|$)/g,
        '',
      );
      // Promote the Drafted Responses section to the canonical "Generated"
      // section. The trailing parenthetical ("(AI-authored from your
      // knowledge base)") is redundant once the per-item suffix already
      // says the same thing, so drop it.
      out = out.replace(
        /##\s*🪄?\s*Drafted Responses\s*\(AI-authored from your knowledge base\)/g,
        '## ✍️ Generated Tender Responses',
      );
      out = out.replace(
        /##\s*Drafted Responses[^\n]*/g,
        '## Generated Tender Responses',
      );
      // Per-item suffix: drafted items already say "Drafted by AI from your
      // knowledge base" — restate as "Generated…" for consistency.
      out = out
        .replace(/_Drafted by AI from your knowledge base/g, '_Generated from your knowledge base')
        .replace(/_Refined from your draft\._/g, '_Generated from your knowledge base._');
    }

    // Inject question text into empty numbered headings. Backend emits
    // "### N. {question}" but when the question field is missing on the
    // refined_questions entry, we get "### N." with nothing after. Look up
    // the question text from finalReport's structured data and splice it in.
    const refined = finalReport?.refined_responses || [];
    const drafted = finalReport?.drafted_responses || [];
    const ordered = [...refined, ...drafted];
    if (ordered.length > 0) {
      out = out.replace(/^(###\s+)(\d+)\.\s*$/gm, (_match, heading, num) => {
        const idx = parseInt(num, 10) - 1;
        const item = ordered[idx];
        const qText = item?.question?.trim();
        return qText ? `${heading}${num}. ${qText}` : `${heading}${num}.`;
      });
    }

    return out;
  };

  // Update editable content when finalReport changes
  React.useEffect(() => {
    if (finalReport?.full_report_markdown) {
      const cleanedContent = cleanMarkdownContent(finalReport.full_report_markdown);
      setEditableContent(cleanedContent);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finalReport?.full_report_markdown, isGenerateMode, finalReport?.refined_responses, finalReport?.drafted_responses]);

  // Navigate to section when activeSection changes
  React.useEffect(() => {
    if (activeSection && contentRef.current) {
      const searchTerms = {
        'criteria': ['evaluation criteria', 'criteria', 'assessment criteria'],
        'justification': ['submission justification', 'justification', 'rationale', 'executive summary'],
        'responses': ['refined responses', 'responses', 'answers', 'our responses'],
        // Phase B — extended targets for the new structured sections.
        'drafted': ['drafted responses', 'drafted answers', 'drafted by ai', 'auto-drafted'],
        'compliance': ['compliance acknowledgements', 'compliance acknowledgments', 'declarations', 'sign'],
        'ai_disclosure': ['ai use disclosure', 'ppn-035', 'ai disclosure', 'model: '],
      };

      const terms = searchTerms[activeSection as keyof typeof searchTerms] || [activeSection];
      
      for (const term of terms) {
        if (scrollToSection(term)) {
          break; // Stop searching once we find and scroll to a section
        }
      }
    }
  }, [activeSection]);

  // Phase D — search highlight uses a tracked-ref + data attribute toggle
  // instead of direct style mutation. Two upgrades over the legacy path:
  //   1. We always clear the previous highlight before applying a new one,
  //      so re-renders / repeated searches never leave orphaned highlights.
  //   2. The styling lives in a CSS rule via the data attribute (see
  //      `data-final-report-highlight`), which is React-friendly and survives
  //      stylesheet changes.
  const highlightedRef = useRef<HTMLElement | null>(null);

  const _clearHighlight = () => {
    const prev = highlightedRef.current;
    if (prev) {
      prev.removeAttribute('data-final-report-highlight');
      highlightedRef.current = null;
    }
  };

  const scrollToSection = (searchTerm: string): boolean => {
    if (!contentRef.current) return false;

    const container = contentRef.current;
    const walker = document.createTreeWalker(
      container,
      NodeFilter.SHOW_TEXT,
      null
    );

    let node = walker.nextNode();
    while (node) {
      if (node.textContent?.toLowerCase().includes(searchTerm.toLowerCase())) {
        const element = node.parentElement;
        if (element) {
          const elementTop = element.offsetTop;
          container.scrollTo({
            top: Math.max(0, elementTop - 100),
            behavior: 'smooth',
          });

          // Apply highlight via attribute (style rule defined globally),
          // tracking the element so subsequent searches clear it cleanly.
          _clearHighlight();
          element.setAttribute('data-final-report-highlight', 'true');
          highlightedRef.current = element;
          window.setTimeout(() => {
            if (highlightedRef.current === element) {
              _clearHighlight();
            }
          }, 2000);

          return true;
        }
      }
      node = walker.nextNode();
    }
    return false;
  };

  // Toggle between edit and preview mode
  const handleToggleEdit = () => {
    setIsEditing(!isEditing);
  };

  // Search functionality for markdown content - simple highlighting by scrolling to content
  const handleSearch = (term: string) => {
    if (!term.trim() || !contentRef.current) return;
    
    // Simple text search within the container
    const container = contentRef.current;
    const walker = document.createTreeWalker(
      container,
      NodeFilter.SHOW_TEXT,
      null
    );
    
    let node = walker.nextNode();
    while (node) {
      if (node.textContent?.toLowerCase().includes(term.toLowerCase())) {
        const element = node.parentElement;
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          break;
        }
      }
      node = walker.nextNode();
    }
  };

  if (!finalReport) {
    return (
      <ViewerContainer>
        <Toolbar>
          <ToolbarLeft>
            <ToolbarTitle>
              <FileText size={16} />
              Final Report Viewer
            </ToolbarTitle>
          </ToolbarLeft>
        </Toolbar>
        <ContentArea>
          <MarkdownContainer>
            <MarkdownContent>
              <div style={{ 
                textAlign: 'center', 
                color: theme.colors.textMuted, 
                padding: theme.spacing[8],
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                minHeight: 'calc(80vh - 120px)'
              }}>
                <FileText size={48} style={{ marginBottom: theme.spacing[4] }} />
                <h3>AI Analysis in Progress</h3>
                <p>Your comprehensive AI-powered tender analysis will appear here once processing is complete. This includes intelligent insights, optimized responses, and strategic recommendations.</p>
              </div>
            </MarkdownContent>
          </MarkdownContainer>
        </ContentArea>
      </ViewerContainer>
    );
  }

  return (
    <ViewerContainer data-print-root="final-report">
      <PrintStyles />
      <Toolbar data-print="hide">
        <ToolbarLeft>
          <ToolbarTitle>
            <FileText size={16} />
            Final Report - {finalReport.date}
          </ToolbarTitle>
        </ToolbarLeft>

        <SearchContainer>
          <SearchIcon>
            <Search size={14} />
          </SearchIcon>
          <SearchInput
            type="text"
            placeholder="Search in report..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              if (e.target.value.trim()) {
                handleSearch(e.target.value);
              }
            }}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && searchTerm.trim()) {
                handleSearch(searchTerm);
              }
            }}
          />
        </SearchContainer>

        <ToolbarRight>
          <ToolbarButton onClick={() => setChecklistOpen((s) => !s)}>
            {hasFailures ? (
              <AlertTriangle size={14} color={theme.colors.error[600]} />
            ) : (
              <CheckCircle2 size={14} color={theme.colors.success[600]} />
            )}
            Readiness ({readinessChecks.length})
            {checklistOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
          </ToolbarButton>

          <ToolbarButton onClick={handleToggleEdit}>
            {isEditing ? <Eye size={14} /> : <Edit3 size={14} />}
            {isEditing ? 'Preview' : 'Edit'}
          </ToolbarButton>

          <ToolbarButton onClick={handleCopy}>
            {copied ? <Check size={14} /> : <Copy size={14} />}
            {copied ? 'Copied!' : 'Copy'}
          </ToolbarButton>

          {/* <ToolbarButton onClick={handlePrint}>
            <Printer size={14} />
            Print / PDF
          </ToolbarButton> */}

          <ToolbarButton onClick={handleDownload} title="Download as Markdown">
            <Download size={14} />
            .md
          </ToolbarButton>

          <ToolbarButton
            $variant="primary"
            onClick={handleDownloadDocx}
            disabled={downloadingDocx}
            title="Download as Word document"
          >
            <FileType2 size={14} />
            {downloadingDocx ? 'Building…' : 'Word (.docx)'}
          </ToolbarButton>
        </ToolbarRight>
      </Toolbar>

      {checklistOpen && (
        <ReadinessPanel data-print="hide">
          <ReadinessHeader>
            <strong>Pre-submission readiness</strong>
            <span style={{ color: theme.colors.textMuted, fontSize: theme.typography.fontSize.xs }}>
              {readinessChecks.filter((c) => c.severity === 'pass').length} pass ·{' '}
              {readinessChecks.filter((c) => c.severity === 'warn').length} warn ·{' '}
              {readinessChecks.filter((c) => c.severity === 'fail').length} fail
            </span>
          </ReadinessHeader>
          <ReadinessList>
            {readinessChecks.map((c) => (
              <ReadinessRow key={c.id} $severity={c.severity}>
                {c.severity === 'pass' && <CheckCircle2 size={14} />}
                {c.severity === 'warn' && <AlertCircle size={14} />}
                {c.severity === 'fail' && <AlertTriangle size={14} />}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600 }}>{c.label}</div>
                  <div style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.textSecondary }}>
                    {c.detail}
                  </div>
                </div>
              </ReadinessRow>
            ))}
          </ReadinessList>
          {hasFailures && (
            <div style={{ display: 'flex', gap: theme.spacing[2], alignItems: 'center', flexWrap: 'wrap' }}>
              <strong style={{ color: theme.colors.error[700], fontSize: theme.typography.fontSize.sm }}>
                Hard failures must be reviewed before exporting.
              </strong>
              <ToolbarButton onClick={() => setWaivedFailures(true)}>
                I have reviewed — allow export
              </ToolbarButton>
              {waivedFailures && (
                <span style={{ color: theme.colors.success[700], fontSize: theme.typography.fontSize.xs }}>
                  Waiver acknowledged.
                </span>
              )}
            </div>
          )}
          {docxError && (
            <div style={{ color: theme.colors.error[700], fontSize: theme.typography.fontSize.sm }}>
              Word export failed: {docxError}
            </div>
          )}
        </ReadinessPanel>
      )}

      <ContentArea>
        <MarkdownContainer ref={contentRef}>
          {isEditing ? (
            <EditableTextarea
              value={editableContent}
              onChange={(e) => setEditableContent(e.target.value)}
              placeholder="Enter your markdown content here..."
            />
          ) : (
            <MarkdownContent>
              <ReactMarkdown 
                remarkPlugins={[remarkGfm]}
                components={{
                  h1: ({ children }) => <h1>{children}</h1>,
                  h2: ({ children }) => <h2>{children}</h2>,
                  h3: ({ children }) => <h3>{children}</h3>,
                  p: ({ children }) => <p>{children}</p>,
                  ul: ({ children }) => <ul>{children}</ul>,
                  ol: ({ children }) => <ol>{children}</ol>,
                  li: ({ children }) => <li>{children}</li>,
                  strong: ({ children }) => <strong>{children}</strong>,
                  em: ({ children }) => <em>{children}</em>,
                  code: ({ children }) => <code>{children}</code>,
                  pre: ({ children }) => <pre>{children}</pre>,
                  blockquote: ({ children }) => <blockquote>{children}</blockquote>,
                  hr: () => <hr />,
                  table: ({ children }) => <table>{children}</table>,
                  thead: ({ children }) => <thead>{children}</thead>,
                  tbody: ({ children }) => <tbody>{children}</tbody>,
                  tr: ({ children }) => <tr>{children}</tr>,
                  th: ({ children }) => <th>{children}</th>,
                  td: ({ children }) => <td>{children}</td>,
                  a: ({ href, children }) => <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>
                }}
              >
                {editableContent ||
`_The full report markdown will appear here once the analysis pipeline finishes._

Use the side panel to navigate the structured summary. Drafted answers, compliance items, and KB-evidence grounding are all surfaced there even before the markdown projection is generated.`}
              </ReactMarkdown>
            </MarkdownContent>
          )}
        </MarkdownContainer>
      </ContentArea>

      {downloadStatus && (
        <StatusMessage $type="success">
          <Check size={16} />
          {downloadStatus}
        </StatusMessage>
      )}
    </ViewerContainer>
  );
};

export default FinalReportViewer;